
import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  Bar,
  BarChart,
  ResponsiveContainer,
} from "recharts";

const PASS_COLOR = "#198754";
const FAIL_COLOR = "#dc3545";
const SELECTED_COLOR = "#2563eb";

const QualityGateResultChart = ({
  projectId,
  timeRange = "all",
  onDateClick = null,
  useLocalTime = true,
}) => {
  const [gateResults, setGateResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const isClickable = !!onDateClick && !!projectId;

  useEffect(() => {
    if (projectId) fetchGateResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchGateResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/quality-gate/${projectId}/results/`);
      if (!response.ok) throw new Error("Failed to fetch results");
      const data = await response.json();
      setGateResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------
  // Time-range helper (FIXED: always returns {start, end})
  // ----------------------------
  const getDateRange = (range, useLocal) => {
    const now = new Date();
    let start = new Date(now);
    let end = new Date(now);

    const setHoursFn = useLocal ? "setHours" : "setUTCHours";
    const setDateFn = useLocal ? "setDate" : "setUTCDate";
    const setFullYearFn = useLocal ? "setFullYear" : "setUTCFullYear";

    // End default: end of today
    end[setHoursFn](23, 59, 59, 999);

    switch (range) {
      case "today":
        start[setHoursFn](0, 0, 0, 0);
        break;

      case "yesterday":
        start[setDateFn](now.getDate() - 1);
        start[setHoursFn](0, 0, 0, 0);
        end[setDateFn](now.getDate() - 1);
        end[setHoursFn](23, 59, 59, 999);
        break;

      case "2d":
        start[setDateFn](now.getDate() - 1);
        start[setHoursFn](0, 0, 0, 0);
        break;

      case "7d":
        start[setDateFn](now.getDate() - 6);
        start[setHoursFn](0, 0, 0, 0);
        break;

      case "30d":
        start[setDateFn](now.getDate() - 29);
        start[setHoursFn](0, 0, 0, 0);
        break;

      case "90d":
        start[setDateFn](now.getDate() - 89);
        start[setHoursFn](0, 0, 0, 0);
        break;

      case "1y":
        start[setFullYearFn](now.getFullYear() - 1);
        start[setHoursFn](0, 0, 0, 0);
        break;

      case "all":
      default:
        return { start: null, end: null };
    }

    return { start, end };
  };

  // ----------------------------
  // One bar per execution (NO grouping)
  // processedData includes commit_id
  // ----------------------------
  const processedData = useMemo(() => {
    if (!gateResults.length) return [];

    const { start: startDate, end: endDate } = getDateRange(timeRange, useLocalTime);

    return gateResults
      .filter((r) => {
        const executed = new Date(r.executed_at);
        return (!startDate || executed >= startDate) && (!endDate || executed <= endDate);
      })
      .map((r) => ({
        created_at: r.executed_at,
        passed: r.passed ? 1 : 0,
        failed: r.passed ? 0 : 1,

        // IMPORTANT: keep commit info here
        commit_id: r.commit_id || r.commit || null,

        // optional metadata
        profile_name: r.profile_name,
        profile_id: r.profile_id,
        passed_bool: !!r.passed,
      }))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [gateResults, timeRange, useLocalTime]);

  // ----------------------------
  // Formatting
  // ----------------------------
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    if (processedData.length > 50) {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ----------------------------
  // Click handling (RELIABLE: Cell-level click)
  // ----------------------------
  const handleEntryClick = (entry) => {
    if (!isClickable) return;

    if (!entry?.commit_id) {
      console.warn("Clicked entry has no commit_id:", entry);
      return;
    }

    setSelectedDataPoint(entry.created_at);
    onDateClick(entry, projectId); // entry has commit_id
  };

  // Allow clicking X-axis label too
  const handleDateClick = (label) => {
    if (!isClickable) return;
    const entry = processedData.find((d) => d.created_at === label);
    if (entry) handleEntryClick(entry);
  };

  // ----------------------------
  // Custom Tick / Tooltip
  // ----------------------------
  const CustomXAxisTick = ({ x, y, payload }) => {
    const isSelected = selectedDataPoint === payload.value;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          dy={16}
          textAnchor="middle"
          fontSize={11}
          fill={isSelected ? SELECTED_COLOR : "#666"}
          style={{
            cursor: isClickable ? "pointer" : "default",
            fontWeight: isSelected ? "bold" : "normal",
            textDecoration: isClickable ? "underline" : "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleDateClick(payload.value);
          }}
        >
          {formatDate(payload.value)}
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const passed = payload.find((p) => p.dataKey === "passed")?.value || 0;
    const failed = payload.find((p) => p.dataKey === "failed")?.value || 0;
    const total = passed + failed;

    const entry = processedData.find((d) => d.created_at === label);
    const commitShort = entry?.commit_id ? `${entry.commit_id.slice(0, 8)}…` : "N/A";
    const isSelected = selectedDataPoint === label;

    return (
      <div
        style={{
          background: "white",
          padding: "15px",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          minWidth: "240px",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            marginBottom: "10px",
            color: isClickable ? SELECTED_COLOR : "#333",
            borderBottom: "1px solid #eee",
            paddingBottom: "8px",
            cursor: isClickable ? "pointer" : "default",
            textDecoration: isClickable ? "underline" : "none",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
          onClick={() => {
            if (entry) handleEntryClick(entry);
          }}
          title={isClickable ? "Click to open Quality Delta" : ""}
        >
          <span>{formatDate(label)}</span>
          {isClickable && (
            <span
              style={{
                fontSize: "12px",
                background: "#f1f5f9",
                padding: "2px 6px",
                borderRadius: "4px",
                whiteSpace: "nowrap",
              }}
            >
              {isSelected ? "✓ Selected" : "Click to view"}
            </span>
          )}
        </div>

        <div style={{ fontSize: "13px" }}>
          <div>
            <b>Commit:</b> {commitShort}
          </div>
          <div>
            <b>Total:</b> {total}
          </div>
          {passed > 0 && <div style={{ color: PASS_COLOR }}>✓ Passed: {passed}</div>}
          {failed > 0 && <div style={{ color: FAIL_COLOR }}>✗ Failed: {failed}</div>}
        </div>
      </div>
    );
  };

  // ----------------------------
  // Render states
  // ----------------------------
  if (loading) {
    return <div className="text-center p-4 text-muted">Loading Quality Gate results…</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-danger">{error}</div>;
  }

  if (processedData.length < 2) {
    return (
      <div className="text-center p-5">
        <h6 className="text-muted">Insufficient Data</h6>
        <p className="text-muted small">At least 2 executions are required to show trends.</p>
      </div>
    );
  }

  const yMax = Math.max(...processedData.map((d) => d.passed + d.failed), 1);

  return (
    <div>
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <defs>
            <linearGradient id="passGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9CCDB8" stopOpacity={1} />
              <stop offset="100%" stopColor="#DAECE4" stopOpacity={1} />
            </linearGradient>
            <linearGradient id="failGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FAAEAC" stopOpacity={1} />
              <stop offset="100%" stopColor="#FDD6D5" stopOpacity={1} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />

          <XAxis dataKey="created_at" tick={<CustomXAxisTick />} interval="preserveStartEnd" height={60} />

          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "#666" }}
            tickFormatter={(value) => Math.floor(value)}
            domain={[0, yMax]}
            tickCount={yMax + 1}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: "20px", fontSize: "12px" }} />

          {/* FAILED */}
          <Bar dataKey="failed" stackId="1" fill="url(#failGradient)" name="Failed" stroke="none" barSize={70}>
            {processedData.map((entry, index) => (
              <Cell
                key={`fail-${index}`}
                cursor={isClickable ? "pointer" : "default"}
                onClick={() => handleEntryClick(entry)}
              />
            ))}
          </Bar>

          {/* PASSED */}
          <Bar dataKey="passed" stackId="1" fill="url(#passGradient)" name="Passed" stroke="none" barSize={70}>
            {processedData.map((entry, index) => (
              <Cell
                key={`pass-${index}`}
                cursor={isClickable ? "pointer" : "default"}
                onClick={() => handleEntryClick(entry)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default QualityGateResultChart;

